import { Arrange, Typography } from "@fullscript/aviary-web";
import type { FormEvent } from "react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Link, Separator } from "@aviary";
import { useAnalyticsUpdateProfileSharedMutation } from "@shared/data/mutations/AnalyticsUpdateProfileMutation/AnalyticsUpdateProfile.mutation.generated";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { useFlippers } from "@shared/hooks/useFlippers/useFlippers";
import { useFsAnalytics } from "@shared/hooks/useFsAnalytics/useFsAnalytics";
import { useIsFullscriptOnly } from "@shared/hooks/useIsFullscriptOnly/useIsFullscriptOnly";
import { useIsRupaRedirectExperience } from "@shared/hooks/useIsRupaRedirectExperience/useIsRupaRedirectExperience";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { useUnauthUrlFields } from "@shared/hooks/useUnauthUrlFields/useUnauthUrlFields";
import { useSearchParams, useLocation } from "@shared/react-router-dom/react-router-dom";
import { PRACTITIONER_MODALITY } from "@shared/types/PractitionerModality";
import { isPureEncapsulationsReferralPartner } from "@unauthenticated/shared/components/ReferralPartnerSignUpBranding/constants";
import { ReferralPartnerSignUpBranding } from "@unauthenticated/shared/components/ReferralPartnerSignUpBranding/ReferralPartnerSignUpBranding";
import { PageOne } from "@unauthenticated/shared/components/SimplifySignUp/PageOne/PageOne";
import { PageTwo } from "@unauthenticated/shared/components/SimplifySignUp/PageTwo/PageTwo";
import { SignUpTriage } from "@unauthenticated/shared/components/SimplifySignUp/SignUpTriage/SignUpTriage";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { l } from "@unauthenticated/shared/locales/i18n";

import { CreateAccountPageOne } from "./CreateAccountPageOne/CreateAccountPageOne";
import { CreateAccountPageTwo } from "./CreateAccountPageTwo/CreateAccountPageTwo";
import { isFormValid } from "./PageOne/helpers";
import type { SimplifySignUpWithSignUpPageProps } from "./types";

import * as styles from "./SimplifySignUp.styles";

const RUPA_HEALTH_LABS_LOGIN_URL = "https://labs.rupahealth.com/login";

// pass formData and setFormData to pages, allow fields to call setFormData

const SignUpPageSelector = (props: SimplifySignUpWithSignUpPageProps) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isEmerson } = useSharedGlobalConfig();
  const [analyticsUpdateProfile] = useAnalyticsUpdateProfileSharedMutation();
  const unauthUrlFields = useUnauthUrlFields();
  const isPeerReferral = !isEmerson && !!props.additionalAttributes?.peerReferralDisplayAttributes;
  const signInLink = isEmerson ? authRoutes.emerson.login : authRoutes.login;
  const { search } = useLocation();
  const step = searchParams.get("step");

  const isFullscriptOnly = useIsFullscriptOnly();
  const isRupaRedirectExperience = useIsRupaRedirectExperience();
  const isSignupTriagePage = !step && !isEmerson;
  const [isReferralPartnerProgramsEnabled] = useFlippers("everest_referral_partner_programs");
  const isPureEncapsulationsSignup = isPureEncapsulationsReferralPartner(props.referralPartnerName);
  const showReferralPartnerBranding =
    isReferralPartnerProgramsEnabled && isPureEncapsulationsSignup && !isEmerson;

  const isFirstStep = () => step === "1";

  const isValidStep = () => {
    return isFirstStep() || step === "2" || !step;
  };

  useEffect(() => {
    if (
      !isValidStep() ||
      (isPeerReferral && !step) ||
      (step === "2" && !isFormValid(props.formData, true))
    ) {
      searchParams.set("step", "1");
      setSearchParams(searchParams);
    } else if (isFirstStep()) {
      props.setFormData(prevData => ({ ...prevData, tosTimestamp: "" }));
    }
  }, [step]);

  const logAnalyticsEvent = useAnalyticsLogger();
  const { logEvent } = useFsAnalytics();
  const hasInteracted = useRef(false);

  const handleFormInteraction = () => {
    if (hasInteracted.current) return;
    hasInteracted.current = true;
    logEvent().authSignupStart({ form_version: props.pageVersion });
  };

  const getPage = () => {
    if (isSignupTriagePage) return <SignUpTriage />;

    if (step === "2") {
      if (isFullscriptOnly) {
        return <CreateAccountPageTwo {...props} />;
      }

      return <PageTwo {...props} />;
    }

    if (isFullscriptOnly) {
      return (
        <CreateAccountPageOne
          onClick={onClick}
          additionalAttributes={props.additionalAttributes}
          {...props}
        />
      );
    }

    return (
      <PageOne onClick={onClick} additionalAttributes={props.additionalAttributes} {...props} />
    );
  };

  const getNextPage = () => {
    if (step === "2") return "1";
    else return "2";
  };

  const onClick = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const nextStep = getNextPage();
    searchParams.set("step", nextStep);
    setSearchParams(searchParams);

    if (isEmerson) {
      logAnalyticsEvent({
        eventName: AnalyticsEvent.EMERSON_SIGNUP_TO_SIGNUP_METHOD,
        payload: {
          ...unauthUrlFields,
          page_version: "simplify_sign_up",
          account_modality: props.formData.practitionerType?.description,
        },
      });
    } else {
      logAnalyticsEvent({
        eventName: AnalyticsEvent.STORE_SIGNUP_TO_SIGNUP_METHOD,
        payload: {
          ...unauthUrlFields,
          page_version: "segment_providers",
          account_modality: isFullscriptOnly
            ? PRACTITIONER_MODALITY.pending
            : props.formData.practitionerType?.description,
          onboarding_segment_experiment: isFullscriptOnly ? "EXPERIMENT" : "CONTROL",
        },
      });
    }

    updateAnalyticsProfile();
  };

  const updateAnalyticsProfile = () => {
    if (Object.keys({ ...unauthUrlFields }).length !== 0) {
      analyticsUpdateProfile({
        variables: {
          input: {
            payload: { ...unauthUrlFields },
          },
        },
      });
    }
  };

  const renderSignInLink = () => {
    if (isRupaRedirectExperience) {
      return (
        <Arrange
          justify="center"
          alignment="center"
          isVertical
          data-testid="form-sign-in-link"
          customCss={styles.rupaRedirectSignInArrange}
        >
          <Typography isMarginless>{t(l.common.RupaRedirectHaveAccountPrompt)}</Typography>
          <Typography isMarginless>
            <Link isColor="success" href={RUPA_HEALTH_LABS_LOGIN_URL} noExternalIcon>
              {t(l.common.RupaRedirectSignInToRupaHealth)}
            </Link>
          </Typography>
          <Typography isMarginless>
            <Link isColor="success" to={signInLink}>
              {t(l.common.RupaRedirectSignInToFullscript)}
            </Link>
          </Typography>
        </Arrange>
      );
    }

    return (
      <Arrange justify="center" data-testid="form-sign-in-link">
        <Typography>
          {t(l.common.HaveAcount)}{" "}
          <Link isColor="success" to={signInLink}>
            {t(l.common.SignIn)}
          </Link>
        </Typography>
      </Arrange>
    );
  };

  const logImAPatientEvent = () => {
    logAnalyticsEvent({
      eventName: AnalyticsEvent.STORE_SIGNUP_IM_A_PATIENT,
      payload: { ...unauthUrlFields },
    });
  };

  const renderPatientLinkOrVideo = () => {
    if (isPeerReferral) {
      return null;
    }
    if (isSignupTriagePage || isFullscriptOnly) return;

    return (
      <Arrange customCss={styles.patientLink} justify="center" data-testid="patient-sign-up-link">
        <Typography isMarginless>
          {t(l.common.IAmAPatient)}{" "}
          <Link
            isColor="success"
            to={{ pathname: authRoutes.patient_sign_up, search }}
            onClick={logImAPatientEvent}
          >
            {t(l.common.FindInvite)}
          </Link>
        </Typography>
      </Arrange>
    );
  };

  const renderPatientLinkOrVideoForPractitioner = () =>
    !isEmerson && (
      <>
        {!tablet.lessThan && isFirstStep() && renderSeparator()}
        {isFirstStep() && renderPatientLinkOrVideo()}
      </>
    );

  const renderSeparator = () => {
    if (isPeerReferral || isSignupTriagePage || step === "2" || isRupaRedirectExperience)
      return null;

    return (
      <div css={styles.dividerBox}>
        <Separator />
      </div>
    );
  };

  const renderReferralPartnerBranding = () => {
    if (!showReferralPartnerBranding || !props.referralPartnerName) return null;

    return <ReferralPartnerSignUpBranding referralPartnerName={props.referralPartnerName} />;
  };

  const renderContent = () => {
    return (
      <>
        {renderReferralPartnerBranding()}
        {getPage()}
        {!!tablet.lessThan && !isFullscriptOnly && renderSignInLink()}
        {!!isFullscriptOnly && step === null && renderSignInLink()}
        {renderPatientLinkOrVideoForPractitioner()}
      </>
    );
  };

  return <div onFocusCapture={handleFormInteraction}>{renderContent()}</div>;
};

export { SignUpPageSelector };
